import { ReactTable, Tooltip } from "@aix/ui"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { Eye, Loader2, Play, Sparkles, Trash2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"

import { trpc } from "../../lib/trpc"

export default function JobsTab() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { tab?: string; monitorSlug?: string; jobId?: string }
  const [monitorSlug, setMonitorSlug] = useState("")
  const [windowDate, setWindowDate] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  // Loading states for buttons
  const [enqueueDailyLoading, setEnqueueDailyLoading] = useState(false)
  const [enqueueSelectedLoading, setEnqueueSelectedLoading] = useState(false)
  const [runNowLoading, setRunNowLoading] = useState<string | null>(null)
  const [extractLoading, setExtractLoading] = useState<string | null>(null)

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [jobToDelete, setJobToDelete] = useState<{ id: string; name: string } | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { data: monitors } = trpc.scraping.listMonitors.useQuery()
  const { data, isLoading, refetch } = trpc.scraping.listJobs.useQuery(
    { monitorSlug: monitorSlug || undefined, windowDate: windowDate || undefined, limit: 100 },
    { keepPreviousData: true },
  )

  const { mutateAsync: createDailyJob } = trpc.scraping.createDailyJob.useMutation()
  const { mutateAsync: processJob } = trpc.scraping.processJob.useMutation()
  const { mutateAsync: bulkCreateDailyJobs } = trpc.scraping.bulkCreateDailyJobs.useMutation()
  const { mutateAsync: deleteJob } = trpc.scraping.deleteJob.useMutation()

  // Extraction mutations
  const { mutateAsync: createExtractionJobs } = trpc.extraction.createExtractionJobs.useMutation()
  const utils = trpc.useUtils()
  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])

  // Set monitor from query params
  useEffect(() => {
    if (search.monitorSlug && search.monitorSlug !== monitorSlug) {
      setMonitorSlug(search.monitorSlug)
    }
  }, [search.monitorSlug])

  const handleEnqueueDaily = async () => {
    if (!monitorSlug) {
      toast.error("Please select a monitor")
      return
    }
    setEnqueueDailyLoading(true)
    try {
      await createDailyJob({
        monitorSlug,
        windowDate: windowDate || today,
        enqueue: true,
      })
      toast.success("Daily job enqueued successfully")
      void refetch()
    } catch (error) {
      toast.error("Failed to enqueue daily job")
      console.error(error)
    } finally {
      setEnqueueDailyLoading(false)
    }
  }

  const handleEnqueueSelected = async () => {
    if (!monitorSlug) {
      toast.error("Please select a monitor")
      return
    }
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates")
      return
    }
    setEnqueueSelectedLoading(true)
    try {
      await bulkCreateDailyJobs({
        monitorSlug,
        startDate,
        endDate,
        spacingSeconds: 60,
        initialDelaySeconds: 2,
        enqueue: true,
      })
      toast.success(`Bulk jobs enqueued for ${startDate} to ${endDate}`)
      void refetch()
    } catch (error) {
      toast.error("Failed to enqueue bulk jobs")
      console.error(error)
    } finally {
      setEnqueueSelectedLoading(false)
    }
  }

  const handleRunNow = async (jobId: string) => {
    setRunNowLoading(jobId)
    try {
      await processJob({ jobId })
      toast.success("Job processing started")
      void refetch()
    } catch (error) {
      toast.error("Failed to start job processing")
      console.error(error)
    } finally {
      setRunNowLoading(null)
    }
  }

  const handleDeleteClick = (jobId: string, monitorName: string) => {
    setJobToDelete({ id: jobId, name: `${monitorName} (${jobId.slice(0, 8)}...)` })
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!jobToDelete) return

    setDeleteLoading(true)
    try {
      await deleteJob({ jobId: jobToDelete.id })
      toast.success("Job deleted successfully")
      void refetch()
      setDeleteModalOpen(false)
      setJobToDelete(null)
    } catch (error) {
      toast.error("Failed to delete job")
      console.error(error)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false)
    setJobToDelete(null)
  }

  const handleExtract = async (jobId: string, monitorSlug: string) => {
    // Get templates for this specific monitor
    let templates: any[] = []
    try {
      const response = await utils.extraction.getTemplatesByMonitor.fetch({ monitorSlug })
      templates = response || []
    } catch (error) {
      console.error("Failed to load monitor templates:", error)
    }

    if (templates.length === 0) {
      toast.error(`No extraction templates configured for monitor: ${monitorSlug}`)
      return
    }

    setExtractLoading(jobId)
    try {
      const templateIds = templates.map((t) => t.id)
      await createExtractionJobs({
        scrapingJobId: jobId,
        templateIds,
        enqueue: true,
      })
      toast.success(
        `Started ${templateIds.length} extraction job${
          templateIds.length === 1 ? "" : "s"
        } using templates: ${templateIds.join(", ")}`,
      )
      // Navigate to extraction tab
      navigate({
        to: "/scraping",
        // @ts-ignore
        search: { ...search, tab: "extraction-jobs" },
      })
    } catch (error) {
      toast.error("Failed to start extraction jobs")
      console.error(error)
    } finally {
      setExtractLoading(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
          <span className="text-primary">Loading jobs...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold  mb-2">Jobs</h2>
        <p className="text-sm text-primary">Manage and monitor your scraping jobs</p>
      </div>

      {/* Filters and Controls */}
      <div className="bg-foregroundx p-4 rounded-lg space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Monitor</label>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={monitorSlug}
              onChange={(e) => setMonitorSlug(e.target.value)}
            >
              <option value="">All monitors</option>
              {monitors?.map((m: any) => (
                <option key={m.slug} value={m.slug}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Filter by Date (PST)</label>
            <input
              type="date"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={windowDate}
              onChange={(e) => setWindowDate(e.target.value)}
            />
          </div>
          <button
            className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-background text-primary  transition-colors"
            onClick={() => refetch()}
          >
            Refresh
          </button>
        </div>

        {/* Single Job Creation */}
        <div className="flex flex-wrap gap-4 items-end pt-4 border-t border-border">
          <div className="flex items-end space-x-2">
            <button
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              onClick={handleEnqueueDaily}
              disabled={enqueueDailyLoading || !monitorSlug}
            >
              {enqueueDailyLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              )}
              {enqueueDailyLoading ? "Enqueuing..." : "Enqueue Daily Job"}
            </button>
            <span className="text-xs text-gray-500">{windowDate ? `for ${windowDate}` : `for today (${today})`}</span>
          </div>
        </div>

        {/* Bulk Job Creation */}
        <div className="flex flex-wrap gap-4 items-end pt-4 border-t border-border">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Backfill Range</label>
            <div className="flex gap-2 items-center">
              <input
                type="date"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start date"
              />
              <span className="text-sm text-gray-500">to</span>
              <input
                type="date"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End date"
              />
            </div>
          </div>
          <button
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-background text-primary  disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            onClick={handleEnqueueSelected}
            disabled={enqueueSelectedLoading || !monitorSlug || !startDate || !endDate}
          >
            {enqueueSelectedLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent mr-2"></div>
            )}
            {enqueueSelectedLoading ? "Enqueuing..." : "Enqueue Date Range"}
          </button>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="bg-background border border-border rounded-lg overflow-hidden">
        <ReactTable
          data={(data || []).map((j: any) => {
            let parsedStats = { itemsScraped: 0, newCount: 0, duplicates: 0 }
            try {
              if (j.stats) {
                parsedStats = JSON.parse(j.stats)
              }
            } catch (e) {
              // Use defaults if stats parsing fails
            }

            return {
              id: j.id,
              fullJobId: j.id,
              created: new Date(j.createdAt).toLocaleString(),
              monitor: j.monitorName || j.monitorSlug || j.monitorId.slice(0, 8),
              monitorSlug: j.monitorSlug,
              platform: j.platform,
              window: j.windowDate || "-",
              status: j.status,
              stats: j.stats,
              scrapedRows: parsedStats.newCount || 0,
              scrapedTotal: parsedStats.itemsScraped || 0,
              duplicates: parsedStats.duplicates || 0,
              debugInfo: {
                jobId: j.id,
                qstashJobId: j.qstashJobId,
                createdAt: j.createdAt,
                startedAt: j.startedAt,
                completedAt: j.completedAt,
                windowStartIso: j.windowStartIso,
                windowEndIso: j.windowEndIso,
                config: j.config,
                status: j.status,
                progress: j.progress,
                error: j.error,
                stats: parsedStats,
                monitorInfo: {
                  name: j.monitorName,
                  slug: j.monitorSlug,
                  id: j.monitorId,
                },
              },
            }
          })}
          columns={[
            {
              Header: "Job ID",
              accessor: "fullJobId",
              Cell: ({ row: { original } }) => (
                <div className="font-mono text-xs  max-w-[100px]">
                  <div className="truncate" title={original.fullJobId}>
                    {original.fullJobId.slice(0, 8)}...
                  </div>
                </div>
              ),
            },
            {
              Header: "Created",
              accessor: "created",
              Cell: ({ row: { original } }) => <div className="text-sm ">{original.created}</div>,
            },
            {
              Header: "Monitor",
              accessor: "monitor",
              Cell: ({ row: { original } }) => <div className="text-sm ">{original.monitor}</div>,
            },
            {
              Header: "Platform",
              accessor: "platform",
              Cell: ({ row: { original } }) => (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {original.platform}
                </span>
              ),
            },
            {
              Header: "Window",
              accessor: "window",
              Cell: ({ row: { original } }) => <div className="text-sm ">{original.window}</div>,
            },
            {
              Header: "Scraped Rows",
              accessor: "scrapedRows",
              Cell: ({ row: { original } }) => (
                <div className="group relative">
                  <div className="text-sm  cursor-help">
                    <span className="font-medium text-green-700">{original.scrapedRows}</span>
                    <span className="text-gray-500 text-xs ml-1">
                      / {original.scrapedTotal}
                      {original.duplicates > 0 && (
                        <span className="text-orange-600 ml-1">({original.duplicates} dup)</span>
                      )}
                    </span>
                  </div>

                  {/* Detailed hover tooltip */}
                  <div className="absolute left-0 bottom-full mb-2 w-80 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[9999] shadow-xl pointer-events-none">
                    <div className="space-y-2">
                      <div className="font-semibold border-b border-gray-700 pb-1 mb-2">Job Debug Info</div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-gray-400">New Results:</span> {original.scrapedRows}
                        </div>
                        <div>
                          <span className="text-gray-400">Total Processed:</span> {original.scrapedTotal}
                        </div>
                        <div>
                          <span className="text-gray-400">Duplicates:</span> {original.duplicates}
                        </div>
                        <div>
                          <span className="text-gray-400">Progress:</span> {original.debugInfo.progress || 0}%
                        </div>
                      </div>

                      <div className="border-t border-gray-700 pt-2">
                        <div className="text-gray-400 text-xs mb-1">Timing:</div>
                        <div className="text-xs space-y-1">
                          <div>
                            Created:{" "}
                            {original.debugInfo.createdAt
                              ? new Date(original.debugInfo.createdAt).toLocaleString()
                              : "-"}
                          </div>
                          <div>
                            Started:{" "}
                            {original.debugInfo.startedAt
                              ? new Date(original.debugInfo.startedAt).toLocaleString()
                              : "-"}
                          </div>
                          <div>
                            Completed:{" "}
                            {original.debugInfo.completedAt
                              ? new Date(original.debugInfo.completedAt).toLocaleString()
                              : "-"}
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-gray-700 pt-2">
                        <div className="text-gray-400 text-xs mb-1">Window:</div>
                        <div className="text-xs">
                          <div>Start: {original.debugInfo.windowStartIso || "-"}</div>
                          <div>End: {original.debugInfo.windowEndIso || "-"}</div>
                        </div>
                      </div>

                      {original.debugInfo.error && (
                        <div className="border-t border-gray-700 pt-2">
                          <div className="text-red-400 text-xs mb-1">Error:</div>
                          <div className="text-xs text-red-300">{original.debugInfo.error}</div>
                        </div>
                      )}

                      <div className="border-t border-gray-700 pt-2">
                        <div className="text-gray-400 text-xs mb-1">Configuration:</div>
                        <div className="text-xs">
                          <div>
                            Monitor: {original.debugInfo.monitorInfo.name} ({original.debugInfo.monitorInfo.slug})
                          </div>
                          {original.debugInfo.config && (
                            <div className="mt-1 p-2 bg-gray-800 rounded text-xs font-mono">
                              <pre className="whitespace-pre-wrap break-all">
                                {JSON.stringify(original.debugInfo.config, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="border-t border-gray-700 pt-2">
                        <div className="text-gray-400 text-xs mb-1">IDs:</div>
                        <div className="text-xs font-mono break-all">
                          <div>Job: {original.debugInfo.jobId}</div>
                          {original.debugInfo.qstashJobId && <div>QStash: {original.debugInfo.qstashJobId}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ),
            },
            {
              Header: "Status",
              accessor: "status",
              Cell: ({ row: { original } }) => {
                const statusColors = {
                  pending: "bg-foregroundx text-gray-800",
                  queued: "bg-yellow-100 text-yellow-800",
                  running: "bg-blue-100 text-blue-800 animate-pulse",
                  completed: "bg-green-100 text-green-800",
                  failed: "bg-red-100 text-destructive",
                }
                return (
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      statusColors[original.status as keyof typeof statusColors] || "bg-foregroundx text-gray-800"
                    }`}
                  >
                    {original.status}
                  </span>
                )
              },
            },
            {
              Header: "Actions",
              accessor: "id",
              Cell: ({ row: { original } }) => (
                <div className="flex gap-2">
                  {/* View Results */}
                  <button
                    className="inline-flex items-center p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    onClick={() =>
                      navigate({
                        to: "/scraping",
                        // @ts-ignore
                        search: { ...search, tab: "results", jobId: original.id },
                      })
                    }
                    title="View Results"
                  >
                    <Eye className="w-4 h-4" />
                  </button>

                  {/* Extract with AI */}
                  {original.status === "completed" && original.scrapedRows > 0 && (
                    <button
                      className="inline-flex items-center p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleExtract(original.id, original.monitorSlug)}
                      disabled={extractLoading === original.id}
                      title="Extract with AI"
                    >
                      {extractLoading === original.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                    </button>
                  )}

                  {/* Run Now */}
                  <button
                    className="inline-flex items-center p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleRunNow(original.id)}
                    disabled={runNowLoading === original.id}
                    title="Run Now"
                  >
                    {runNowLoading === original.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>

                  {/* Delete */}
                  <button
                    className="inline-flex items-center p-1.5 text-gray-500 hover:text-destructive hover:bg-red-50 rounded transition-colors"
                    onClick={() => handleDeleteClick(original.id, original.monitor)}
                    title="Delete Job"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ),
            },
          ]}
          enableSorting
          enablePagination
        />
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium ">Delete Job</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to delete this scraping job? This action cannot be undone.
                  </p>
                </div>
              </div>

              {jobToDelete && (
                <div className="bg-foregroundx rounded-md p-3 mb-4">
                  <div className="text-sm">
                    <span className="font-medium ">Job:</span> {jobToDelete.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">All associated results will also be deleted.</div>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 text-sm font-medium text-primary bg-background border border-gray-300 rounded-md  focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  onClick={handleDeleteCancel}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                >
                  {deleteLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  )}
                  {deleteLoading ? "Deleting..." : "Delete Job"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
